/**
 * /saas/vendor-watch — Vendor Spend Watch dashboard (SaaS pack).
 *
 * Read-only spend view: total MTD, per-vendor spend vs budget, runway,
 * and alerts. For adding / editing accounts and AI optimization insights,
 * use Setup → API Costs.
 *
 * Talks to the same endpoints as /settings/api-costs:
 *   GET /api/vendor-watch/accounts   → accounts + snapshots + alerts
 *   GET /api/vendor-watch/alerts     → all active alerts
 *   POST /api/vendor-watch/sync-all  → manual sync
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import WarningIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SettingsIcon from "@mui/icons-material/Settings";
import { useState } from "react";

import { api } from "@/lib/api";

export const Route = createFileRoute("/_auth/saas/vendor-watch")({
  component: VendorWatchPage,
});

// ─── types (mirror api-costs.tsx) ────────────────────────────────────────

type AlertOut = {
  alert_type: string;
  severity: "info" | "warning" | "critical";
  triggered_at: string;
  message: string;
};

type AutoRechargeView = {
  charges_this_month: number | null;
  days_per_charge: number | null;
  forecast_month_cents: number | null;
  variance_vs_budget_pct: number | null;
};

type SnapshotOut = {
  period_start: string;
  period_end: string;
  amount_cents: number;
  daily_burn_cents: number;
  projected_days_to_zero: number | null;
  captured_at: string;
  next_billing_date: string | null;
  _adapter_variant: string | null;
  _warning: string | null;
};

type AccountOut = {
  id: string;
  vendor: string;
  account_label: string;
  is_mission_critical: boolean;
  is_active: boolean;
  credit_balance_cents: number | null;
  credit_balance_updated_at: string | null;
  monthly_budget_cents: number | null;
  auto_recharge_view: AutoRechargeView;
  thresholds: {
    low_balance_cents: number;
    min_days_runway: number;
    mtd_threshold_cents: number;
  };
  last_synced_at: string | null;
  last_sync_status: "never" | "ok" | "error";
  last_sync_error: string | null;
  latest_snapshot: SnapshotOut | null;
  active_alerts: AlertOut[];
};

// ─── helpers ──────────────────────────────────────────────────────────────

function dollars(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function pct(v: number | null | undefined): string {
  if (v == null) return "—";
  return `${v > 0 ? "+" : ""}${v.toFixed(1)}%`;
}

function ago(iso: string | null | undefined): string {
  if (!iso) return "never";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ─── component ────────────────────────────────────────────────────────────

function VendorWatchPage() {
  const qc = useQueryClient();
  const [alertsExpanded, setAlertsExpanded] = useState(true);

  const accountsQuery = useQuery({
    queryKey: ["vendor-watch", "accounts"],
    queryFn: () => api<{ accounts: AccountOut[]; count: number }>("/api/vendor-watch/accounts"),
    refetchInterval: 5 * 60 * 1000,
  });

  const alertsQuery = useQuery({
    queryKey: ["vendor-watch", "alerts"],
    queryFn: () =>
      api<{
        alerts: Array<AlertOut & { account: string; critical: boolean }>;
      }>("/api/vendor-watch/alerts"),
  });

  const syncAll = useMutation({
    mutationFn: () => api("/api/vendor-watch/sync-all", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vendor-watch"] }),
  });

  const accounts = accountsQuery.data?.accounts ?? [];
  const allAlerts = alertsQuery.data?.alerts ?? [];
  const totalMtdCents = accounts.reduce(
    (s, a) => s + (a.latest_snapshot?.amount_cents ?? 0),
    0,
  );

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              Vendor Spend Watch
            </h1>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Real-time AI &amp; cloud vendor spend — budgets, alerts, runway
            </span>
          </div>
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<SyncIcon />}
              onClick={() => syncAll.mutate()}
              disabled={syncAll.isPending || accounts.length === 0}
              variant="outlined"
              size="small"
            >
              {syncAll.isPending ? "Syncing…" : "Sync all"}
            </Button>
            <Button
              component={Link}
              to="/settings/api-costs"
              startIcon={<SettingsIcon />}
              variant="outlined"
              size="small"
            >
              Configure vendors
            </Button>
          </Stack>
        </Stack>
      </div>

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4 space-y-4">
        {/* Alerts banner */}
        {allAlerts.length > 0 && (
          <Card sx={{ borderLeft: 4, borderColor: allAlerts.some((a) => a.severity === "critical") ? "error.main" : "warning.main" }}>
            <CardHeader
              avatar={
                allAlerts.some((a) => a.severity === "critical")
                  ? <ErrorIcon color="error" />
                  : <WarningIcon color="warning" />
              }
              title={
                <Typography variant="subtitle1">
                  {allAlerts.length} active alert{allAlerts.length === 1 ? "" : "s"}
                  {allAlerts.some((a) => a.severity === "critical") && (
                    <Chip
                      size="small"
                      color="error"
                      label={`${allAlerts.filter((a) => a.severity === "critical").length} critical`}
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
              }
              action={
                <IconButton
                  size="small"
                  onClick={() => setAlertsExpanded((v) => !v)}
                >
                  {alertsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              }
              onClick={() => setAlertsExpanded((v) => !v)}
              sx={{ cursor: "pointer" }}
            />
            {alertsExpanded && (
              <CardContent sx={{ pt: 0 }}>
                <Stack spacing={1}>
                  {allAlerts.map((a, i) => (
                    <Alert key={i} severity={a.severity === "critical" ? "error" : a.severity === "warning" ? "warning" : "info"}>
                      <strong>{a.account}</strong> — {a.message}
                    </Alert>
                  ))}
                </Stack>
              </CardContent>
            )}
          </Card>
        )}

        {/* Headline tiles */}
        <Card>
          <CardContent>
            <Stack direction="row" spacing={4} alignItems="baseline">
              <Box>
                <Typography variant="caption" color="text.secondary">Total MTD spend</Typography>
                <Typography variant="h4">{dollars(totalMtdCents)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Accounts monitored</Typography>
                <Typography variant="h6">{accounts.length}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Mission-critical</Typography>
                <Typography variant="h6">{accounts.filter((a) => a.is_mission_critical).length}</Typography>
              </Box>
              <Box sx={{ ml: "auto" }}>
                <Typography variant="caption" color="text.secondary">
                  {accountsQuery.dataUpdatedAt
                    ? `Refreshed ${ago(new Date(accountsQuery.dataUpdatedAt).toISOString())}`
                    : ""}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Accounts table */}
        <Card>
          <CardHeader title="Monitored accounts" />
          <CardContent sx={{ p: 0 }}>
            {accountsQuery.isLoading ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <CircularProgress />
              </Box>
            ) : accounts.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary" gutterBottom>
                  No vendor accounts configured yet.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Set up spend monitoring via the CFO Advisor chat — type{" "}
                  <code style={{ fontSize: 12 }}>"Set up Anthropic spend monitoring with key sk-ant-admin-…"</code>
                  {" "}— or add accounts in{" "}
                  <Link to="/settings/api-costs" className="underline text-indigo-600">
                    Setup → API Costs
                  </Link>
                  .
                </Typography>
                <Button
                  component={Link}
                  to="/settings/api-costs"
                  variant="contained"
                  size="small"
                >
                  Go to API Costs →
                </Button>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Account</TableCell>
                    <TableCell align="right">MTD spend</TableCell>
                    <TableCell align="right">Forecast / Budget</TableCell>
                    <TableCell align="right">Runway</TableCell>
                    <TableCell>Last sync</TableCell>
                    <TableCell>Alerts</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.map((a) => {
                    const ar = a.auto_recharge_view;
                    const overBudget = (ar.variance_vs_budget_pct ?? 0) > 20;
                    return (
                      <TableRow key={a.id} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2">
                              <strong>{a.vendor}</strong>/{a.account_label}
                            </Typography>
                            {a.is_mission_critical && (
                              <Chip size="small" color="error" label="CRITICAL" sx={{ fontSize: "0.65rem" }} />
                            )}
                            {!a.is_active && <Chip size="small" label="inactive" />}
                          </Stack>
                        </TableCell>

                        <TableCell align="right">
                          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
                            <Typography variant="body2">{dollars(a.latest_snapshot?.amount_cents)}</Typography>
                            {a.latest_snapshot?._warning && (
                              <Tooltip title={a.latest_snapshot._warning} arrow>
                                <WarningIcon fontSize="small" color="warning" sx={{ fontSize: 14, cursor: "help" }} />
                              </Tooltip>
                            )}
                          </Stack>
                          {a.latest_snapshot?.daily_burn_cents != null && (
                            <Typography variant="caption" color="text.secondary">
                              {dollars(a.latest_snapshot.daily_burn_cents)}/day
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell align="right">
                          <Typography variant="body2" color={overBudget ? "error.main" : "text.primary"}>
                            {dollars(ar.forecast_month_cents)} / {dollars(a.monthly_budget_cents)}
                          </Typography>
                          {ar.variance_vs_budget_pct != null && (
                            <Typography variant="caption" color={overBudget ? "error.main" : "text.secondary"}>
                              {pct(ar.variance_vs_budget_pct)}
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell align="right">
                          <Typography variant="body2">
                            {a.latest_snapshot?.projected_days_to_zero != null
                              ? `${a.latest_snapshot.projected_days_to_zero.toFixed(1)}d`
                              : "—"}
                          </Typography>
                          {ar.charges_this_month != null && (
                            <Typography variant="caption" color="text.secondary">
                              {ar.charges_this_month} charges
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell>
                          <Tooltip title={a.last_sync_error || ""}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Chip
                                size="small"
                                color={
                                  a.last_sync_status === "error"
                                    ? "error"
                                    : a.last_sync_status === "ok"
                                    ? "success"
                                    : "default"
                                }
                                label={a.last_sync_status}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {ago(a.last_synced_at)}
                              </Typography>
                            </Stack>
                          </Tooltip>
                        </TableCell>

                        <TableCell>
                          {a.active_alerts.length === 0 ? (
                            <Chip size="small" label="clear" variant="outlined" />
                          ) : (
                            <Stack direction="row" spacing={0.5}>
                              {a.active_alerts.map((al, i) => (
                                <Tooltip key={i} title={al.message} arrow>
                                  <Chip
                                    size="small"
                                    color={al.severity === "critical" ? "error" : al.severity === "warning" ? "warning" : "info"}
                                    label={al.alert_type.replace(/_/g, " ")}
                                    sx={{ fontSize: "0.6rem" }}
                                  />
                                </Tooltip>
                              ))}
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Footer hint */}
        {accounts.length > 0 && (
          <div className="text-xs text-slate-500 dark:text-slate-400 pb-4">
            To add vendors, set budgets, or adjust alert thresholds →{" "}
            <Link to="/settings/api-costs" className="underline text-indigo-600">
              Setup → API Costs
            </Link>
            . You can also add vendors by typing{" "}
            <span className="font-mono">"Set up [vendor] spend monitoring with key …"</span>
            {" "}in the CFO Advisor chat.
          </div>
        )}
      </div>
    </div>
  );
}
